import { ArrowUpRight, Info, TriangleAlert } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { confirmationMessage, fieldGroup } from "@/lib/confirmation-message"
import { cn } from "@/lib/utils"

type FieldType =
  | "text"
  | "textarea"
  | "date"
  | "time"
  | "phone"
  | "email"
  | "currency"
  | "url"
  | "select"
  | "multi_select"
  | "address"

interface Field {
  label: string
  value: string
  confidence?: "high" | "medium" | "low" | null
  url?: string | null
  type?: FieldType | null
  options?: string[] | null
  readonly?: boolean
  readonly_reason?: string | null
}

interface Section {
  label?: string | null
  fields: Field[]
}

interface Action {
  label: string
  message: string
  variant?: "default" | "outline"
}

// IMP-365: a dedicated alert channel for agent instructions / errors /
// follow-up actions (e.g. a connectivity failure), so prose never lands in a
// field slot.
interface Notice {
  level?: "info" | "warning" | "error" | null
  message: string
}

interface FieldsDisplayData {
  title: string
  subtitle?: string | null
  sections: Section[]
  actions?: Action[]
  notice?: Notice | null
}

function NoticeAlert({ notice }: { notice: Notice }) {
  const level = notice.level ?? "warning"
  const styles = {
    info: "border-blue-300 bg-blue-50 text-blue-900 dark:border-blue-400/30 dark:bg-blue-400/10 dark:text-blue-200",
    warning:
      "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-400/10 dark:text-amber-200",
    error:
      "border-red-300 bg-red-50 text-red-900 dark:border-red-400/30 dark:bg-red-400/10 dark:text-red-200",
  }[level]
  const Icon = level === "info" ? Info : TriangleAlert
  return (
    <div
      className={cn(
        "mb-4 flex items-start gap-2 rounded-lg border p-3 text-sm",
        styles,
      )}
    >
      <Icon className="mt-0.5 size-4 shrink-0" />
      <p className="whitespace-pre-wrap">{notice.message}</p>
    </div>
  )
}

interface FieldsDisplayArtifactProps {
  data: string
  onAction?: (message: string) => void
}

function fieldKey(si: number, fi: number) {
  return `${si}-${fi}`
}

function parseAddress(value: string) {
  const parts = value.split(",").map((p) => p.trim())
  return {
    street: parts[0] || "",
    city: parts[1] || "",
    state: parts[2] || "",
    zip: parts[3] || "",
  }
}

function formatAddress(addr: {
  street: string
  city: string
  state: string
  zip: string
}) {
  return [addr.street, addr.city, addr.state, addr.zip]
    .filter(Boolean)
    .join(", ")
}

function formatAddressMultiline(value: string) {
  const { street, city, state, zip } = parseAddress(value)
  const cityLine = [city, [state, zip].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ")
  return [street, cityLine].filter(Boolean).join("\n")
}

function InlineEditor({
  field,
  value,
  onCommit,
  onCancel,
}: {
  field: Field
  value: string
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const inferredType =
    !field.type && /description/i.test(field.label) ? "textarea" : field.type
  const fieldType = inferredType ?? "text"

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      onCancel()
    } else if (e.key === "Enter" && fieldType !== "textarea") {
      e.preventDefault()
      onCommit((e.target as HTMLInputElement).value)
    }
  }

  switch (fieldType) {
    case "textarea":
      return (
        <Textarea
          defaultValue={value}
          rows={3}
          autoFocus
          onBlur={(e) => onCommit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.preventDefault()
              onCancel()
            }
          }}
          className="text-sm"
        />
      )

    case "select":
      return (
        <Select defaultValue={value} onValueChange={(val) => onCommit(val)}>
          <SelectTrigger size="sm" className="h-7 w-full text-sm" autoFocus>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(field.options ?? []).map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )

    case "multi_select":
      return (
        <MultiSelectEditor
          value={value}
          options={field.options ?? []}
          onCommit={onCommit}
          onCancel={onCancel}
        />
      )

    case "address":
      return (
        <AddressEditor value={value} onCommit={onCommit} onCancel={onCancel} />
      )

    case "currency":
      return (
        <div className="flex items-center gap-1">
          <span className="text-muted-foreground text-sm">$</span>
          <Input
            type="text"
            defaultValue={value.replace(/^\$/, "")}
            autoFocus
            onBlur={(e) => onCommit(e.target.value)}
            onKeyDown={handleKeyDown}
            className="h-7 text-sm"
          />
        </div>
      )

    default: {
      const inputType =
        fieldType === "phone"
          ? "tel"
          : fieldType === "email"
            ? "email"
            : fieldType === "date"
              ? "date"
              : fieldType === "time"
                ? "time"
                : fieldType === "url"
                  ? "url"
                  : "text"
      return (
        <Input
          type={inputType}
          defaultValue={value}
          autoFocus
          onBlur={(e) => onCommit(e.target.value)}
          onKeyDown={handleKeyDown}
          className="h-7 text-sm"
        />
      )
    }
  }
}

function MultiSelectEditor({
  value,
  options,
  onCommit,
  onCancel,
}: {
  value: string
  options: string[]
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const selected = value
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
  const [checked, setChecked] = useState<Set<string>>(new Set(selected))
  const containerRef = useRef<HTMLDivElement>(null)

  const commitValue = useCallback(() => {
    onCommit(Array.from(checked).join(", "))
  }, [checked, onCommit])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleFocusOut = (e: FocusEvent) => {
      if (!container.contains(e.relatedTarget as Node)) {
        commitValue()
      }
    }

    container.addEventListener("focusout", handleFocusOut)
    return () => container.removeEventListener("focusout", handleFocusOut)
  }, [commitValue])

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-1.5"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault()
          onCancel()
        }
      }}
    >
      {options.map((opt) => (
        <label key={opt} className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={checked.has(opt)}
            onCheckedChange={(isChecked) => {
              setChecked((prev) => {
                const next = new Set(prev)
                if (isChecked) next.add(opt)
                else next.delete(opt)
                return next
              })
            }}
          />
          {opt}
        </label>
      ))}
    </div>
  )
}

function AddressEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: string
  onCommit: (value: string) => void
  onCancel: () => void
}) {
  const addr = parseAddress(value)
  const [fields, setFields] = useState(addr)
  const containerRef = useRef<HTMLDivElement>(null)

  const commitValue = useCallback(() => {
    onCommit(formatAddress(fields))
  }, [fields, onCommit])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleFocusOut = (e: FocusEvent) => {
      if (!container.contains(e.relatedTarget as Node)) {
        commitValue()
      }
    }

    container.addEventListener("focusout", handleFocusOut)
    return () => container.removeEventListener("focusout", handleFocusOut)
  }, [commitValue])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault()
      onCancel()
    } else if (e.key === "Enter") {
      e.preventDefault()
      commitValue()
    }
  }

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      <Input
        placeholder="Street"
        defaultValue={fields.street}
        autoFocus
        onChange={(e) => setFields((f) => ({ ...f, street: e.target.value }))}
        onKeyDown={handleKeyDown}
        className="h-8 text-sm"
      />
      <div className="flex gap-1.5">
        <Input
          placeholder="City"
          defaultValue={fields.city}
          onChange={(e) => setFields((f) => ({ ...f, city: e.target.value }))}
          onKeyDown={handleKeyDown}
          className="h-8 text-sm"
        />
        <Input
          placeholder="State"
          defaultValue={fields.state}
          onChange={(e) => setFields((f) => ({ ...f, state: e.target.value }))}
          onKeyDown={handleKeyDown}
          className="h-8 flex-shrink-0 basis-16 text-sm"
        />
        <Input
          placeholder="ZIP"
          defaultValue={fields.zip}
          onChange={(e) => setFields((f) => ({ ...f, zip: e.target.value }))}
          onKeyDown={handleKeyDown}
          className="h-8 flex-shrink-0 basis-20 text-sm"
        />
      </div>
    </div>
  )
}

export function FieldsDisplayArtifact({
  data,
  onAction,
}: FieldsDisplayArtifactProps) {
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [editedValues, setEditedValues] = useState<Record<string, string>>({})
  const [finalized, setFinalized] = useState(false)
  const isReadOnly = finalized || !onAction

  let parsed: FieldsDisplayData

  try {
    parsed = JSON.parse(data.trim()) as FieldsDisplayData
  } catch {
    return <code className="text-sm">{data}</code>
  }

  const getDisplayValue = (field: Field, key: string) => {
    return key in editedValues ? editedValues[key] : field.value
  }

  const handleCommit = (key: string, value: string) => {
    setEditedValues((prev) => ({ ...prev, [key]: value }))
    setEditingKey(null)
  }

  const handleCancel = () => {
    setEditingKey(null)
  }

  const isEditable = (field: Field) => {
    return !isReadOnly && !field.readonly && !field.url
  }

  // IMP-362: the confirmation message summarizes the user's action and only the
  // fields they actually changed — not the entire submission (that whole dump
  // was the regression Chris flagged). With edits, the header reads "<label>
  // with the following changes:" followed by the changed "Key: Value" lines
  // grouped per section ("Section — Subject"). With no edits the action label
  // alone (e.g. "Create contacts") is the whole message.
  const buildMessage = (label: string) => {
    const groups = parsed.sections.map((section, si) => {
      const pairs = section.fields
        .map((field, fi) => ({ field, key: fieldKey(si, fi) }))
        .filter(({ key }) => key in editedValues)
        .map(({ field, key }) => ({
          label: field.label,
          value: editedValues[key],
        }))
        .filter((p) => p.value != null && String(p.value).trim() !== "")
      if (pairs.length === 0) return ""
      const subject = [section.label, parsed.subtitle]
        .filter(Boolean)
        .join(" — ")
      return fieldGroup(subject || null, pairs)
    })
    const hasChanges = groups.some(Boolean)
    const header = hasChanges ? `${label} with the following changes:` : label
    return confirmationMessage(header, groups, "\n\n")
  }

  const hasEditableFields = parsed.sections.some((section) =>
    section.fields.some((field) => isEditable(field)),
  )

  return (
    <Card className="my-3 gap-0 py-0">
      <CardHeader className="gap-0.5 px-4 pt-4 pb-3">
        <div className="text-muted-foreground text-xs font-medium tracking-wide">
          {parsed.title}
        </div>
        {parsed.subtitle && (
          <div className="text-base font-semibold">{parsed.subtitle}</div>
        )}
      </CardHeader>
      <CardContent className="px-4 pb-4">
        {parsed.notice?.message && <NoticeAlert notice={parsed.notice} />}
        {parsed.sections.map((section, si) => (
          <div key={si} className={si > 0 ? "mt-5" : ""}>
            {section.label && (
              <p className="text-foreground pb-2 text-sm font-semibold">
                {section.label}
              </p>
            )}
            <div>
              {section.fields.map((field, fi) => {
                const key = fieldKey(si, fi)
                const isEditing = editingKey === key
                const editable = isEditable(field)
                const displayValue = getDisplayValue(field, key)
                const isAddress = field.type === "address"

                return (
                  <div
                    key={fi}
                    className={cn(
                      "flex min-h-9 items-start gap-8 py-2",
                      fi < section.fields.length - 1 && "border-b",
                    )}
                  >
                    <span className="text-muted-foreground w-36 shrink-0 pt-0.5 text-sm">
                      {field.label}
                      {field.readonly && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="text-muted-foreground ml-1 inline size-3 cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent>
                            {field.readonly_reason ??
                              "This field cannot be edited"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>

                    {isEditing ? (
                      <div className="min-w-0 flex-1">
                        <InlineEditor
                          field={field}
                          value={displayValue}
                          onCommit={(val) => handleCommit(key, val)}
                          onCancel={handleCancel}
                        />
                      </div>
                    ) : (
                      <span
                        className={cn(
                          "flex min-w-0 flex-1 items-start gap-1.5 pt-0.5 text-left text-sm font-medium",
                          editable &&
                            "hover:bg-muted/50 -mx-2 -my-0.5 cursor-pointer rounded px-2 py-0.5",
                        )}
                        onClick={() => {
                          if (editable) {
                            setEditingKey(key)
                          }
                        }}
                      >
                        {field.url ? (
                          <a
                            href={field.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                          >
                            {displayValue || "\u2014"}
                            <ArrowUpRight className="size-3.5 shrink-0" />
                          </a>
                        ) : displayValue ? (
                          <span
                            className={cn(
                              "min-w-0 break-words",
                              isAddress && "whitespace-pre-line",
                            )}
                          >
                            {isAddress
                              ? formatAddressMultiline(displayValue)
                              : displayValue}
                          </span>
                        ) : (
                          <span className="text-muted-foreground italic">
                            (empty)
                          </span>
                        )}
                        {field.confidence === "low" && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <TriangleAlert className="mt-0.5 size-3.5 shrink-0 cursor-help text-amber-500" />
                            </TooltipTrigger>
                            <TooltipContent>
                              Low confidence — verify this value
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </CardContent>
      {!isReadOnly && parsed.actions && parsed.actions.length > 0 && (
        <CardFooter className="flex-col items-start gap-2 border-t px-4 py-3">
          {hasEditableFields && (
            <p className="text-muted-foreground text-xs">
              Tap any field to edit
            </p>
          )}
          <div className="flex gap-2">
            {parsed.actions.map((action, ai) => (
              <Button
                key={ai}
                // Standardize on primary + ghost only: the first (confirm)
                // action is primary, the rest (e.g. Cancel) ghost. The agent's
                // `variant` is intentionally ignored (IMP-112).
                variant={ai === 0 ? "default" : "ghost"}
                size="sm"
                onClick={() => {
                  setFinalized(true)
                  setEditingKey(null)
                  onAction?.(buildMessage(action.label))
                }}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </CardFooter>
      )}
    </Card>
  )
}
